// Convert a coach-selected date (Date | string | number) to a stable "day anchor" timestamp.
// We store 12:00:00.000Z (UTC noon) of that calendar day.
export function getDayAnchorUtcNoon(input) {
  if (!input) return null
  const d = new Date(input)
  if (isNaN(d)) throw new Error('Invalid date: ' + input)

  // Extract Y/M/D from the local selection (the calendar day they picked)
  const y = d.getFullYear()
  const m = d.getMonth() // 0-based
  const day = d.getDate()

  // Store the canonical instant: that day's UTC NOON
  return Date.UTC(y, m, day, 12, 0, 0, 0)
}

// Given a stored anchor timestamp, return a Date for the UI DatePicker
export function fromDayAnchorToLocalDate(anchorMs) {
  if (!anchorMs && anchorMs !== 0) return null
  const anchor = new Date(anchorMs)
  if (isNaN(anchor)) return null

  // If this is a lenient start date (stored at 10:00 UTC / -14h offset),
  // we add 14 hours to get back to the 'visual' calendar date in UTC.
  // We check for hour 10 specifically to maintain backward compatibility with old 'Noon' dates.
  if (anchor.getUTCHours() === 10) {
    anchor.setHours(anchor.getHours() + 14)
  }

  // Pull Y/M/D in UTC, then construct a *local* date with those components.
  const y = anchor.getUTCFullYear()
  const m = anchor.getUTCMonth()
  const day = anchor.getUTCDate()

  return new Date(y, m, day) // local midnight for that same Y/M/D
}

export function fromExpirationAnchorToLocalDate(anchorMs) {
  if (!anchorMs && anchorMs !== 0) return null

  const d = fromDayAnchorToLocalDate(anchorMs)
  if (!d) return null

  // Both old logic (Noon next day) and new logic (11:59 UTC next day)
  // fall on the "Next Day" in UTC terms.
  // So we simply subtract one day to show the user the day they originally selected.
  d.setDate(d.getDate() - 1)
  return d
}

// Convert a coach-selected date to the earliest possible start timestamp (UTC+14 start of day)
// 00:00 in UTC+14 is 10:00 AM of the previous day in UTC.
export function getLenientStartDate(input) {
  if (!input) return null
  const d = new Date(input)
  if (isNaN(d)) throw new Error('Invalid date: ' + input)

  const y = d.getFullYear()
  const m = d.getMonth()
  const day = d.getDate()

  // Subtract 14 hours from UTC midnight to reach start of day in UTC+14
  return Date.UTC(y, m, day, -14, 0, 0, 0)
}

// Convert a coach-selected date to the latest possible expiration timestamp (UTC-12 end of day)
// 23:59:59 in UTC-12 is 11:59:59 of the NEXT day in UTC.
export function getLenientExpirationDate(input) {
  if (!input) return null
  const d = new Date(input)
  if (isNaN(d)) throw new Error('Invalid date: ' + input)

  const y = d.getFullYear()
  const m = d.getMonth()
  const day = d.getDate()

  // Add 12 hours to the END of the UTC day (23:59 + 12h)
  return Date.UTC(y, m, day, 23 + 12, 59, 59, 999)
}
